// Vue core libraries - Pinia & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

import LocalForagePlugin, { crosswalk } from "@/plugins/LocalForagePlugin";
import type { TickerCrosswalkEntry } from "@/services/data/sync/ticker-crosswalk-sync-coordinator";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

/**
 * C5 (Option 1): the in-memory half of the content-id -> symbol crosswalk, mirroring the shape of
 * `stores/Tickers.ts`. Loaded from the `crosswalk` LocalForage store (kept current by
 * `ticker-crosswalk-sync-coordinator.ts`) and consulted synchronously from `content-hydration.ts`
 * via `CompositionContext.getTickerSymbolByContentId`.
 */

// State values
const initialized = computed< boolean >( () => symbolsByContentId.value.size > 0 );
const loading = ref< boolean >( false );

const symbolsByContentId = ref< Map< string, string > >( new Map() );

export const useTickerCrosswalkStore = defineStore( 'ticker-crosswalk', () => {
    function reset(): void {
        symbolsByContentId.value = new Map();
        loading.value = false;
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ TickerCrosswalk ].initialize() exiting - already processing...' );
            return;
        }

        try {
            logger.debug( `[ TickerCrosswalk ] Checking the local database for any crosswalk entries...` );

            loading.value = true;

            const t0: number = performance.now();

            // Wait for the driver to settle — unconditionally. The bindings are assigned synchronously,
            // but an instance's own ready() resolves before sibling stores finish upgrading the shared
            // database on a fresh origin; reading in that window hangs localforage (fresh-origin boot race).
            await LocalForagePlugin.initialize();

            await crosswalk.ready();
            const numberEntries = await crosswalk.length();

            if ( numberEntries > 0 ) {
                const hydrated = new Map< string, string >();

                await crosswalk.iterate( ( entry: TickerCrosswalkEntry ) => {
                    hydrated.set( entry.id, entry.symbol );
                    return;
                } );

                symbolsByContentId.value = hydrated;

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ hydrated.size } ticker crosswalk entries in ${ t1 - t0 } ms` );
            }
        } catch ( err: any ) {
            logger.error( JSON.stringify( err.message ) );
            reset();
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    function symbolByContentId( id: string ): string | undefined {
        return symbolsByContentId.value.get( id );
    }

    return {
        // ACTIONS
        initialize,
        reset,
        symbolByContentId,

        // GETTERS
        initialized
    };
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useTickerCrosswalkStore, import.meta.hot ) );
}
